import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * IV&V checkpoints + assessments surface (T-0066 / T-0067 / E06-F04 + F05).
 *
 *   - listCheckpoints(engagementId, { status })
 *       GET /api/v1/contract/checkpoints?engagement_id=&status=
 *       Returns checkpoints in SOW order (sow_number ASC, unnumbered last,
 *       then sequence). Each row carries the read-time joins the tab needs:
 *       linked milestone + due_date, source citation, latest scoring round.
 *
 *   - listCheckpointAssessments(checkpointId)
 *       GET /api/v1/contract/checkpoints/:checkpoint_id/assessments
 *       Full scoring history for one checkpoint (drawer + scoring modal).
 *
 * Schemas mirror the service-layer DTOs in
 * `apps/api/src/services/checkpoints.ts` and
 * `apps/api/src/services/checkpoint-assessments.ts`.
 */

export const CheckpointCriterionSchema = z.object({
  criterion_id: z.string(),
  label: z.string(),
  score_type: z.enum(['numeric', 'pass_fail', 'ryg']),
  weight: z.number(),
});
export type CheckpointCriterion = z.infer<typeof CheckpointCriterionSchema>;

export const OverallPostureSchema = z.enum(['green', 'yellow_divergent', 'red', 'grey']);
export type OverallPosture = z.infer<typeof OverallPostureSchema>;

export const CheckpointSchema = z.object({
  checkpoint_id: z.string().uuid(),
  engagement_id: z.string().uuid(),
  name: z.string(),
  sequence: z.number(),
  sow_number: z.number().nullable(),
  purpose: z.string().nullable(),
  sow_timing: z.string().nullable(),
  target_date: z.string().nullable(),
  schedule_milestone_id: z.string().nullable(),
  milestone: z.object({ milestone_id: z.string(), name: z.string(), date: z.string() }).nullable(),
  due_date: z.string().nullable(),
  criteria: z.array(CheckpointCriterionSchema),
  status: z.enum(['active', 'archived']),
  source: z.enum(['ai_proposed_confirmed', 'template_seeded', 'manually_added']),
  methodology_template_version_id: z.string().nullable(),
  ai_proposal_id: z.string().nullable(),
  source_citation: z
    .object({
      citation_id: z.string(),
      document_title: z.string(),
      page_number: z.number().nullable(),
      section_path: z.string().nullable(),
      confidence: z.number(),
    })
    .nullable(),
  latest_assessment: z
    .object({
      assessment_id: z.string(),
      round_seq: z.number(),
      status: z.enum(['in_progress', 'submitted']),
      overall_posture: OverallPostureSchema.nullable(),
      submitted_at: z.string().nullable(),
      created_at: z.string(),
    })
    .nullable(),
  rounds_count: z.number(),
  archived_at: z.string().nullable(),
  created_by: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Checkpoint = z.infer<typeof CheckpointSchema>;

export const CheckpointListSchema = z.object({
  items: z.array(CheckpointSchema),
});
export type CheckpointList = z.infer<typeof CheckpointListSchema>;

export const AssessmentSummarySchema = z.object({
  assessment_id: z.string().uuid(),
  round_seq: z.number(),
  status: z.enum(['in_progress', 'submitted']),
  overall_posture: OverallPostureSchema.nullable(),
  submitted_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AssessmentSummary = z.infer<typeof AssessmentSummarySchema>;

export const AssessmentListSchema = z.object({
  items: z.array(AssessmentSummarySchema),
});
export type AssessmentList = z.infer<typeof AssessmentListSchema>;

export interface ListCheckpointsOpts {
  status?: 'active' | 'archived';
}

export function listCheckpoints(
  engagementId: string,
  opts: ListCheckpointsOpts = {},
): Promise<CheckpointList> {
  return apiClient(`/api/v1/contract/checkpoints`, CheckpointListSchema, {
    query: {
      engagement_id: engagementId,
      ...(opts.status ? { status: opts.status } : {}),
    },
  });
}

export function useCheckpointsListQuery(
  engagementId: string | undefined,
  opts: ListCheckpointsOpts = {},
) {
  return useQuery({
    queryKey: ['checkpoints', engagementId, opts] as const,
    queryFn: () => listCheckpoints(engagementId!, opts),
    enabled: Boolean(engagementId),
    staleTime: 30_000,
  });
}

export function listCheckpointAssessments(checkpointId: string): Promise<AssessmentList> {
  return apiClient(
    `/api/v1/contract/checkpoints/${encodeURIComponent(checkpointId)}/assessments`,
    AssessmentListSchema,
  );
}

export function useCheckpointAssessmentsQuery(checkpointId: string | undefined) {
  return useQuery({
    queryKey: ['checkpoint-assessments', checkpointId] as const,
    queryFn: () => listCheckpointAssessments(checkpointId!),
    enabled: Boolean(checkpointId),
    staleTime: 30_000,
  });
}

// ===========================================================================
// Checkpoint mutations (T-0066) — create / edit / archive from the
// checkpoint drawer. There is no delete: the API only archives.
// ===========================================================================

export interface PatchCheckpointBody {
  name?: string;
  purpose?: string | null;
  target_date?: string | null;
  schedule_milestone_id?: string | null;
  criteria?: Array<CheckpointCriterion>;
}

const CheckpointResponseSchema = z.object({}).passthrough();

export function patchCheckpoint(checkpointId: string, body: PatchCheckpointBody): Promise<unknown> {
  return apiClient(
    `/api/v1/contract/checkpoints/${encodeURIComponent(checkpointId)}`,
    CheckpointResponseSchema,
    { method: 'PATCH', body },
  );
}

export function usePatchCheckpointMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ checkpointId, body }: { checkpointId: string; body: PatchCheckpointBody }) =>
      patchCheckpoint(checkpointId, body),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['checkpoints', engagementId] });
      }
    },
  });
}

export function archiveCheckpoint(checkpointId: string): Promise<unknown> {
  return apiClient(
    `/api/v1/contract/checkpoints/${encodeURIComponent(checkpointId)}/archive`,
    CheckpointResponseSchema,
    { method: 'POST' },
  );
}

export function useArchiveCheckpointMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (checkpointId: string) => archiveCheckpoint(checkpointId),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['checkpoints', engagementId] });
      }
    },
  });
}

export interface CreateCheckpointBody {
  engagement_id: string;
  name: string;
  sequence: number;
  purpose?: string | null;
  target_date?: string | null;
  schedule_milestone_id?: string | null;
  criteria: Array<Omit<CheckpointCriterion, 'criterion_id'>>;
  source: 'manually_added';
}

export function createCheckpoint(body: CreateCheckpointBody): Promise<unknown> {
  return apiClient('/api/v1/contract/checkpoints', CheckpointResponseSchema, {
    method: 'POST',
    body,
  });
}

export function useCreateCheckpointMutation(engagementId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCheckpointBody) => createCheckpoint(body),
    onSuccess: () => {
      if (engagementId) {
        void qc.invalidateQueries({ queryKey: ['checkpoints', engagementId] });
      }
    },
  });
}

// ===========================================================================
// Assessment scoring round (Tier 2 — start / score / submit, T-0067)
// ===========================================================================

export const AssessmentScoreSchema = z.object({
  criterion_id: z.string(),
  score_value: z.union([z.string(), z.number()]),
  score_type: z.string().optional(),
  scored_at: z.string().optional(),
  override_record_id: z.string().nullable().optional(),
});
export type AssessmentScore = z.infer<typeof AssessmentScoreSchema>;

/**
 * An AI suggestion for one criterion of a scoring round. `suggested_value`
 * is set only for categorical criteria (pass/fail, red/yellow/green); for
 * numeric criteria it is always null and `assessment` carries how the
 * evidence reads, so the consultant chooses the number.
 */
export const ScoreCandidateSchema = z.object({
  ai_proposal_id: z.string().uuid(),
  criterion_id: z.string(),
  suggested_value: z.string().nullable(),
  assessment: z.string(),
  rationale: z.string(),
  confidence: z.number(),
  citations: z.array(
    z.object({
      document_id: z.string(),
      document_title: z.string(),
      page_number: z.number().nullable(),
      section_path: z.string().nullable(),
    }),
  ),
});
export type ScoreCandidate = z.infer<typeof ScoreCandidateSchema>;

/** A contracted deliverable a checkpoint depends on, with its scoring evidence. */
export const LinkedDeliverableSchema = z.object({
  register_entry_id: z.string(),
  title: z.string(),
  status: z.string(),
  expected_delivery_date: z.string().nullable(),
  source: z.enum(['ai_confirmed', 'manual']),
  /** A vendor milestone named as an IV&V gate (T-0217); optional so an older server parses. */
  is_ivv_gate: z.boolean().nullable().optional(),
  submission_reading: z
    .object({
      document_title: z.string().nullable(),
      met: z.number(),
      partially_met: z.number(),
      not_met: z.number(),
      not_evidenced: z.number(),
    })
    .nullable(),
  open_deviations: z.array(
    z.object({ deviation_entry_id: z.string(), title: z.string(), severity: z.string() }),
  ),
});
export type LinkedDeliverable = z.infer<typeof LinkedDeliverableSchema>;

const CheckpointDeliverablesSchema = z.object({
  items: z.array(LinkedDeliverableSchema),
  pending_proposal: z.boolean().optional(),
});

const linksPath = (checkpointId: string) =>
  `/api/v1/contract/checkpoints/${encodeURIComponent(checkpointId)}/deliverables`;

// Key namespace 'checkpoint-deliverables' is entity-scoped: added to the
// live-refresh sweep in engagement-activity.ts.
export function useCheckpointDeliverablesQuery(checkpointId: string | undefined) {
  return useQuery({
    queryKey: ['checkpoint-deliverables', checkpointId] as const,
    queryFn: () => apiClient(linksPath(checkpointId!), CheckpointDeliverablesSchema),
    enabled: Boolean(checkpointId),
  });
}

export function useSetCheckpointDeliverablesMutation(checkpointId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      apiClient(linksPath(checkpointId), CheckpointDeliverablesSchema, {
        method: 'PUT',
        body: { deliverable_entry_ids: ids },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checkpoint-deliverables', checkpointId] });
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessment'] });
    },
  });
}

export function useSuggestCheckpointDeliverablesMutation(checkpointId: string) {
  return useMutation({
    mutationFn: () =>
      apiClient(`${linksPath(checkpointId)}/suggest`, z.object({ requested: z.boolean() }), {
        method: 'POST',
      }),
  });
}

export const AssessmentDetailSchema = AssessmentSummarySchema.extend({
  engagement_id: z.string().optional(),
  checkpoint_id: z.string().optional(),
  findings: z.string().nullable().optional(),
  recommendations: z.string().nullable().optional(),
  scores: z.array(AssessmentScoreSchema.passthrough()),
  // Optional so an older API build still parses.
  ai_candidates: z.array(ScoreCandidateSchema).optional(),
  linked_deliverables: z.array(z.lazy(() => LinkedDeliverableSchema)).optional(),
}).passthrough();
export type AssessmentDetail = z.infer<typeof AssessmentDetailSchema>;

export function getAssessment(assessmentId: string): Promise<AssessmentDetail> {
  return apiClient(
    `/api/v1/contract/assessments/${encodeURIComponent(assessmentId)}`,
    AssessmentDetailSchema,
  );
}

export function useAssessmentDetailQuery(assessmentId: string | undefined) {
  return useQuery({
    queryKey: ['checkpoint-assessment', assessmentId] as const,
    queryFn: () => getAssessment(assessmentId!),
    enabled: Boolean(assessmentId),
  });
}

const StartRoundResultSchema = z
  .object({ assessment_id: z.string(), round_seq: z.number(), status: z.string() })
  .passthrough();

export function useStartAssessmentRoundMutation(checkpointId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ idempotencyKey }: { idempotencyKey?: string } = {}) =>
      apiClient(
        `/api/v1/contract/checkpoints/${encodeURIComponent(checkpointId!)}/assessments`,
        StartRoundResultSchema,
        { method: 'POST', body: {}, ...(idempotencyKey ? { idempotencyKey } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessments', checkpointId] });
      // The checkpoints list shows each checkpoint's latest round + posture.
      void qc.invalidateQueries({ queryKey: ['checkpoints'] });
    },
  });
}

export interface PatchScoreItem {
  criterion_id: string;
  score_value: string | number;
  override?: { tag: string; rationale: string } | null;
  /** The AI candidate this score accepts unchanged ("Use AI value"). */
  from_ai_proposal_id?: string | null;
}

/**
 * Ask for AI score suggestions on a round in progress (again). They arrive
 * asynchronously on the assessment detail; live refresh picks them up.
 */
export function useRequestScoreCandidatesMutation() {
  return useMutation({
    mutationFn: (assessmentId: string) =>
      apiClient(
        `/api/v1/contract/assessments/${encodeURIComponent(assessmentId)}/ai-candidates`,
        z.object({ assessment_id: z.string(), queued: z.boolean() }).passthrough(),
        { method: 'POST', body: {} },
      ),
  });
}

const PatchScoresResultSchema = z
  .object({ assessment_id: z.string(), completed_criteria: z.number(), total_criteria: z.number() })
  .passthrough();

export function usePatchScoresMutation(checkpointId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assessmentId,
      scores,
      findings,
      recommendations,
    }: {
      assessmentId: string;
      scores: PatchScoreItem[];
      findings?: string | null;
      recommendations?: string | null;
    }) =>
      apiClient(
        `/api/v1/contract/assessments/${encodeURIComponent(assessmentId)}/scores`,
        PatchScoresResultSchema,
        {
          method: 'PATCH',
          body: {
            scores,
            ...(findings !== undefined ? { findings } : {}),
            ...(recommendations !== undefined ? { recommendations } : {}),
          },
        },
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessment', vars.assessmentId] });
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessments', checkpointId] });
      // The checkpoints list shows each checkpoint's latest round + posture.
      void qc.invalidateQueries({ queryKey: ['checkpoints'] });
    },
  });
}

const SubmitAssessmentResultSchema = z
  .object({
    assessment_id: z.string(),
    status: z.string(),
    overall_posture: z.string().nullable(),
    submitted_at: z.string().nullable(),
  })
  .passthrough();

export function useSubmitAssessmentMutation(checkpointId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      assessmentId,
      idempotencyKey,
    }: {
      assessmentId: string;
      idempotencyKey?: string;
    }) =>
      apiClient(
        `/api/v1/contract/assessments/${encodeURIComponent(assessmentId)}/submit`,
        SubmitAssessmentResultSchema,
        { method: 'POST', body: {}, ...(idempotencyKey ? { idempotencyKey } : {}) },
      ),
    onSuccess: (_r, vars) => {
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessment', vars.assessmentId] });
      void qc.invalidateQueries({ queryKey: ['checkpoint-assessments', checkpointId] });
      // The checkpoints list shows each checkpoint's latest round + posture.
      void qc.invalidateQueries({ queryKey: ['checkpoints'] });
    },
  });
}
